import { nanoid } from '@lighthouse/tools'
import type { Fragment, Node as PMNode } from '@tiptap/pm/model'
import { Fragment as PM_Fragment, Slice } from '@tiptap/pm/model'

import { CDN_IMAGE_PREFIX, getImageFullUrlInApplication } from '../../../utils'
import type { SliceSlot } from './types'

export async function webImg2File(imgUrl: string) {
    const blob = await fetch(imgUrl).then(res => res.blob())
    const ext = blob.type.split('/')[1]
    return new File([blob], `${nanoid(10)}.${ext}`, { type: blob.type })
}

export function formatFileSize(bytes: number): string {
    if (bytes >= 1024 * 1024 * 1024) {
        return `${(bytes / (1024 * 1024 * 1024)).toFixed(0)}GB`
    }
    if (bytes >= 1024 * 1024) {
        return `${(bytes / (1024 * 1024)).toFixed(0)}MB`
    }
    if (bytes >= 1024) {
        return `${(bytes / 1024).toFixed(0)}KB`
    }
    return `${bytes}B`
}

function needUploadImage(node: PMNode): boolean {
    return node.type.name === 'image' && !node.attrs.src?.startsWith(CDN_IMAGE_PREFIX)
}

function getImageFile(node: PMNode, appId: string): string {
    const src = node.attrs.src ?? ''
    return src.startsWith('http') ? src : getImageFullUrlInApplication(appId, src)
}

/**
 * 从 slice 中移除需上传的图片，返回新 slice 及每个图片的槽位 offset（用于 Decoration 位置）
 */
export function processSliceRemoveImages(slice: Slice, appId: string): { newSlice: Slice; slots: SliceSlot[] } {
    const slots: SliceSlot[] = []

    function processFragment(frag: Fragment, baseOffset: number): { newFrag: PM_Fragment; nextOffset: number } {
        const nodes: PMNode[] = []
        let offset = baseOffset
        frag.forEach((node: PMNode) => {
            if (needUploadImage(node)) {
                slots.push({ offset, file: getImageFile(node, appId) })
                return
            }
            if (node.content.size > 0) {
                const { newFrag } = processFragment(node.content, offset + 1)
                const newNode = node.copy(newFrag)
                nodes.push(newNode)
                offset += newNode.nodeSize
            } else {
                nodes.push(node)
                offset += node.nodeSize
            }
        })
        return { newFrag: PM_Fragment.fromArray(nodes), nextOffset: offset }
    }

    const { newFrag } = processFragment(slice.content, 0)
    return {
        newSlice: new Slice(newFrag, slice.openStart, slice.openEnd),
        slots
    }
}
